import { Form } from "antd";
import { Alert } from "antd-v5";
import { BaseModalRef } from "components/BaseModal";
import message from "components/CustomMessage";
import Vocabulary from "components/Vocabulary";
import { FindingBucketContextType } from "contexts/FindingBucketContext";
import React, { forwardRef, useCallback } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { AssessmentResponseDto } from "types/assessment";
import { FieldTypes } from "utils/formUtils";
import { formatNumber } from "utils/numberUtil";

import BulkUpdatePopup from "./BulkUpdatePopup";

type Props = {
  afterClose: () => void;
  bucket: FindingBucketContextType;
  history: History;
};

const AddToAssessmentPopup = forwardRef<BaseModalRef, Props>(
  ({ afterClose, bucket, history }, ref) => {
    const [form] = Form.useForm();
    const totalFindingsCount = bucket.totalCount;

    const handleOnOk = async (data: any) => {
      await bucket.submit(data, FindingAPI.bulk.addToAssessment);

      message.success(
        <>
          {totalFindingsCount} finding(s) added to{" "}
          <Vocabulary name="assessment" />. Please check after some time.
        </>,
      );
    };

    const onAssessmentCreation = useCallback(
      (createdAssessmentData) => {
        form.setFieldsValue({
          assessmentId: createdAssessmentData.id,
        });
      },
      [form],
    );

    const fields = [
      {
        name: "",
        render: () => (
          <Alert
            message={
              <>
                Findings that are already associated with a{" "}
                <Vocabulary name="assessment" /> will be removed from old{" "}
                <Vocabulary name="assessment" /> and added to new{" "}
                <Vocabulary name="assessment" />.
                <p />
                Only <Vocabulary name="assessment" /> that correspond to the{" "}
                <Vocabulary name="product" />, <Vocabulary name="subProduct" />{" "}
                and Environment for all selected findings will be shown.
              </>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ),
      },
      {
        name: "assessmentId",
        label: (
          <>
            Select <Vocabulary name="assessment" />
          </>
        ),
        type: FieldTypes.ASSESSMENT_SELECT_ADD,
        props: {
          onCreate: (createdAssessment: AssessmentResponseDto) => {
            onAssessmentCreation(createdAssessment);
          },
          history,
          bucket,
        },
      },
    ];

    const modalProps = {
      okText: (
        <>
          Add to <Vocabulary name="assessment" />
        </>
      ),
    };

    return (
      <BulkUpdatePopup
        ref={ref}
        title={
          <>
            Add to <Vocabulary name="assessment" />
            <div className="pull-right text-bold info">
              {formatNumber(totalFindingsCount, "long")} findings
            </div>
          </>
        }
        form={form}
        onOk={handleOnOk}
        bucket={bucket}
        fields={fields}
        modalProps={modalProps}
        afterClose={afterClose}
      />
    );
  },
);

export default AddToAssessmentPopup;
